import React from 'react';
import {
  Box,
  Heading,
  Icon,
  KeyboardAvoidingView,
  VStack,
  HStack,
  ScrollView,
} from '@gluestack-ui/themed';

import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import {
  MessageCircleWarning, SquarePen, Files, Sparkles,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useSelectedModule } from '@/common/hooks/student/useSelectedModule';
import { TaskTypeEnum } from '@/common/api/models/taskTypeEnum';
import { isMeetingOnlyTask } from '@/common/utils/tasks/isEventDeliveredTask';
import { navigateToModuleScreen } from '@/common/navigation/RoleNavigations/Student/navigateToModuleScreen';
import type { StudentRouteParamList } from '@/common/navigation/RoleNavigations/Student/types';
import InstructionsPanel from '../InstructionsPanel';
import ResponseComposer from '../ResponseComposer';
import ResourceSidebar from '../ResourceSidebar';
import FormativeFeedbackCard from '../components/aiTutor/FormativeFeedbackCard';
import EvaluationCard from '../components/aiTutor/EvaluationCard';
import EventTaskView from '../../module/components/task-types/event/EventTaskView';
import TaskSidebarSelector from '../../module/components/TaskSidebarSelector';
import { OralExamTakenNotice } from '../components/OralExamTakenNotice';
import { useOralExamTaken } from '../hooks/useOralExamTaken';

// eslint-disable-next-line import/no-named-as-default
import useTaskSubmissionController from '../hooks/useTaskSubmissionController';
import { Task, Resource } from '../types';

type TaskMobileTabParamList = {
  InstructionsPanel: undefined;
  ResponseComposer: undefined;
  ResourceSidebar: undefined;
  AiTutor: undefined;
};

const Tab = createBottomTabNavigator<TaskMobileTabParamList>();

interface TaskScreenMobileLayoutProps {
  task: Task;
  resources: Resource[];
  thumbnails: Record<string, string | undefined>;
  testID?: string;
  courseId: string;
  moduleId: string;
  readonly?: boolean;
  isLoading: boolean;
  safetyFuseConflict: boolean;
}

const TaskScreenMobileLayout: React.FC<TaskScreenMobileLayoutProps> = ({
  task,
  resources,
  thumbnails,
  testID,
  courseId,
  moduleId,
  readonly,
  isLoading,
  safetyFuseConflict,
}) => {
  const { t } = useTranslation();
  const isMarkedAsDone = task?.submission?.state === 'marked_as_done';
  const { module, tasks } = useSelectedModule();

  const meetingOnlyTask = isMeetingOnlyTask(task);
  const navigation = useNavigation<NativeStackNavigationProp<StudentRouteParamList>>();
  const isOralExamTaken = useOralExamTaken(task, courseId, moduleId, module?.version);

  const taskScope = { courseId, moduleId, taskId: task.id };
  const { submit, cancelEvaluation } = useTaskSubmissionController({
    taskScope,
    taskType: task.type as TaskTypeEnum,
    formalReq: task.evaluation?.formalCriteria,
    enableAutosave: false, // critical: set to false for all instances except the primary editor (EssayComposer)
  });

  if (isOralExamTaken) {
    return (
      <VStack flex={1} bg="$white" testID={testID}>
        <OralExamTakenNotice onBack={() => navigateToModuleScreen(navigation, courseId, moduleId)} />
      </VStack>
    );
  }

  if (meetingOnlyTask) {
    return (
      <VStack flex={1} bg="$white" testID={testID}>
        <ScrollView flex={1} showsVerticalScrollIndicator={false}>
          <Box px="$5" py="$6">
            <EventTaskView
              task={task}
              module={module}
              tasks={tasks}
              selectedModule={{ courseId, moduleId }}
              resources={resources}
              thumbnails={thumbnails}
              showTitle={false}
              noCard
              poolSurface="full"
            />
          </Box>
        </ScrollView>
      </VStack>
    );
  }

  return (
    <Box flex={1} testID={testID}>
      <Tab.Navigator
        id="taskMobileLayout"
        screenOptions={{
          headerShown: false,
          tabBarStyle: {
            backgroundColor: 'white',
            ...(Platform.OS === 'web' ? { height: 65, paddingBottom: 10 } : {}),
          },
        }}
      >
        <Tab.Screen
          name="InstructionsPanel"
          options={{
            tabBarLabel: t('navigation.taskscreen.instructions'),
            tabBarIcon: ({ color, size }) => <Icon as={MessageCircleWarning} color={color} w={size - 2} h={size - 2} />,
          }}
        >
          {() => (
            <ScrollView flex={1} bg="$white">
              <Box px="$6" pt="$8" pb="$6" testID="instructions-container">
                <Heading mb="$6" testID="instructions-title">
                  {t('navigation.taskscreen.instructions')}
                </Heading>
                <InstructionsPanel testID="instructions-panel" task={task} />
                <Box mt="$6">
                  <TaskSidebarSelector task={task} tasks={tasks} module={module}>
                    {null}
                  </TaskSidebarSelector>
                </Box>
              </Box>
            </ScrollView>
          )}
        </Tab.Screen>
        <Tab.Screen
          name="ResponseComposer"
          options={{
            tabBarLabel: t('navigation.taskscreen.composer'),
            tabBarIcon: ({ color, size }) => <Icon as={SquarePen} color={color} w={size - 2} h={size - 2} />,
          }}
        >
          {() => (
            <KeyboardAvoidingView
              flex={1}
              bg="$white"
              behavior="padding"
              keyboardVerticalOffset={120}
              testID="composer-container"
              px="$5"
              py="$4"
            >
              <ResponseComposer
                key={task?.id}
                task={task}
                readonly={readonly}
                isMobileView
                isLoading={isLoading}
                taskScope={taskScope}
                safetyFuseConflict={safetyFuseConflict}
              />
            </KeyboardAvoidingView>
          )}
        </Tab.Screen>
        <Tab.Screen
          name="ResourceSidebar"
          options={{
            tabBarLabel: t('navigation.taskscreen.resources'),
            tabBarIcon: ({ color, size }) => <Icon as={Files} color={color} w={size - 2} h={size - 2} />,
          }}
        >
          {() => (
            <ScrollView flex={1} bg="$white">
              <Box px="$5" pt="$8" pb="$10" testID="resources-container">
                <Heading mb="$6" testID="resources-title">
                  {t('navigation.taskscreen.resources')}
                </Heading>
                <ResourceSidebar testID="resource-sidebar" resources={resources} thumbnails={thumbnails} />
              </Box>
            </ScrollView>
          )}
        </Tab.Screen>
        {task?.evaluation?.mode === 'auto' && (
          <Tab.Screen
            name="AiTutor"
            options={{
              tabBarLabel: t('navigation.taskscreen.aiTutor', 'AI Tutor'),
              tabBarIcon: ({ color, size }) => <Icon as={Sparkles} color={color} w={size - 2} h={size - 2} />,
            }}
          >
            {() => (
              <KeyboardAvoidingView
                flex={1}
                bg="$white"
                behavior="padding"
                keyboardVerticalOffset={120}
                testID="ai-tutor-container"
              >
                <VStack flex={1} position="relative">
                  <HStack justifyContent="space-between" alignItems="center" p="$4" pb="$3">
                    <HStack space="sm" alignItems="center">
                      <Icon as={Sparkles} size="md" color="$primary600" />
                      <Heading size="sm" color="$textHeading">
                        {t('navigation.taskscreen.aiTutor', 'AI Tutor')}
                      </Heading>
                    </HStack>

                  </HStack>

                  <VStack flex={1}>
                    <Box px="$4" pb="$4">
                      <EvaluationCard
                        onSubmit={() => submit()}
                        onCancelEvaluation={cancelEvaluation}
                        readonly={readonly}
                        isMarkedAsDone={isMarkedAsDone}
                        gated={task?.evaluation?.completionPolicyEffective === 'gated'}
                      />
                    </Box>
                    <Box flex={1} bg="$bgSubtle" borderTopWidth={1} borderColor="$borderLight" p="$4">
                      <FormativeFeedbackCard
                        courseId={taskScope.courseId}
                        moduleId={taskScope.moduleId}
                        taskId={taskScope.taskId}
                        initialFeedback={task?.submission?.evaluationStudentFeedback}
                        readonly={readonly}
                      />
                    </Box>
                  </VStack>
                </VStack>
              </KeyboardAvoidingView>
            )}
          </Tab.Screen>
        )}
      </Tab.Navigator>
    </Box>
  );
};

export default TaskScreenMobileLayout;
